// The trestles under a sculpted bench (docs/claude/lattice.md, *Trestles*): each
// a Stenal trestle (benchSolids.ts `trestlePieces`) standing square to the face
// it is stored on, at its station there. It reaches into the room as far as the
// bench does over it — read off the bench's plan outline, so a cage that pulls
// the front out or in carries the trestle with it — its front post set back
// FRONT_POST_SETBACK from there; its top rail's top is the bench's underside,
// the lowest the solid comes down anywhere over the trestle. Pure.

import type {
  ProductInstance,
  SculptedBench,
  SculptTrestle,
  Vec2,
} from '../shared/types';
import {
  FRONT_POST_SETBACK,
  type Solid,
  TRESTLE_TIMBER,
  trestlePieces,
} from './benchSolids';
import { TRESTLE_WIDTH } from './benchSupports';
import {
  contentMemo,
  HEAVY_FIELDS,
} from './plainData';
import {
  type SculptLayout,
  sculptLayout,
} from './sculptBench';
import {
  facePointAt,
  paramOf,
  sameSculptHost,
  sculptFace,
} from './sculptRun';

/** The least clear height under a bench a trestle is built in (mm): its top
 *  rail and a levelling foot. */
export const SCULPT_TRESTLE_MIN_HEIGHT = TRESTLE_TIMBER + 12;

/** How far a trestle is looked for into the room (mm). */
const MAX_REACH = 3000;

/** One trestle resolved: its plan frame (room local) — its back's middle on
 *  the face, `n` into the room, `u` across it —, how far the bench reaches over
 *  it (`reach`), its front post's front (`front`), the bench's underside over
 *  it (`top`), and its pieces. */
export interface SculptTrestleLayout {
  trestle: SculptTrestle;
  back: Vec2;
  n: Vec2;
  u: Vec2;
  reach: number;
  front: number;
  top: number;
  solids: Solid[];
}

/** Where a ray from `p` along `d` first leaves the ring (its distance), past
 *  `from`; Infinity when it never crosses it there. */
function firstCrossing(ring: readonly Vec2[], p: Vec2, d: Vec2, from: number): number {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const ex = b.x - a.x, ey = b.y - a.y;
    const den = d.x * ey - d.y * ex;
    if (Math.abs(den) < 1e-12) continue;
    const wx = a.x - p.x, wy = a.y - p.y;
    const t = (wx * ey - wy * ex) / den, s = (wx * d.y - wy * d.x) / den;
    if (s >= -1e-9 && s <= 1 + 1e-9 && t > from && t < best) best = t;
  }
  return best;
}

/** The lowest the bench's surface comes over a plan point (mm above the floor),
 *  or null when the point is not under it: a vertical line through each quad
 *  of its mesh. */
function undersideAt(lay: SculptLayout, p: Vec2): number | null {
  const { pts } = lay;
  let low = Infinity;
  const hit = (a: { x: number; y: number; z: number }, b: typeof a, c: typeof a): void => {
    const d = (b.y - c.y) * (a.x - c.x) + (c.x - b.x) * (a.y - c.y);
    if (Math.abs(d) < 1e-9) return;
    const l1 = ((b.y - c.y) * (p.x - c.x) + (c.x - b.x) * (p.y - c.y)) / d;
    const l2 = ((c.y - a.y) * (p.x - c.x) + (a.x - c.x) * (p.y - c.y)) / d;
    const l3 = 1 - l1 - l2;
    if (l1 < -1e-9 || l2 < -1e-9 || l3 < -1e-9) return;
    const z = l1 * a.z + l2 * b.z + l3 * c.z;
    if (z < low) low = z;
  };
  for (let j = 0; j + 1 < pts.length; j++) {
    const r0 = pts[j], r1 = pts[j + 1], n = r0.length;
    for (let k = 0; k < n; k++) {
      const k2 = (k + 1) % n;
      const a = r0[k], b = r1[k], c = r1[k2], d = r0[k2];
      const x0 = Math.min(a.x, b.x, c.x, d.x), x1 = Math.max(a.x, b.x, c.x, d.x);
      const y0 = Math.min(a.y, b.y, c.y, d.y), y1 = Math.max(a.y, b.y, c.y, d.y);
      if (p.x < x0 - 1e-6 || p.x > x1 + 1e-6 || p.y < y0 - 1e-6 || p.y > y1 + 1e-6) continue;
      hit(a, b, c);
      hit(a, c, d);
    }
  }
  return Number.isFinite(low) ? low : null;
}

/** How deep the bench's cross-section nearest a point on its back reaches
 *  along `n` (mm), cages and all. */
function depthAt(lay: SculptLayout, back: Vec2, n: Vec2): number {
  const along = lay.run.locate({ x: back.x + n.x, y: back.y + n.y }).along;
  let j = 0;
  for (let i = 1; i < lay.stations.length; i++) if (Math.abs(lay.stations[i] - along) < Math.abs(lay.stations[j] - along)) j = i;
  let d = 0;
  for (const p of lay.pts[j] ?? []) d = Math.max(d, (p.x - back.x) * n.x + (p.y - back.y) * n.y);
  return d;
}

/** A trestle under a bench resolved, or null when it stands where the bench is
 *  not (off its run, its face gone) or too low a bench leaves no room for it. */
export function sculptTrestleLayout(inst: ProductInstance, lay: SculptLayout, t: SculptTrestle): SculptTrestleLayout | null {
  if (!lay.run.chain.faces.some((f) => sameSculptHost(f.host, t.host))) return null;
  const f = sculptFace(inst, t.host);
  if (!f) return null;
  const p = paramOf(f, t.s);
  const back = facePointAt(f, p, 0), n = f.n, u = f.t;
  const h = TRESTLE_WIDTH / 2;
  const at = (x: number, d: number): Vec2 => ({ x: back.x + u.x * x + n.x * d, y: back.y + u.y * x + n.y * d });
  // As deep as the bench is over all of its width — and no deeper than its
  // cross-section there, so one by a corner does not run on under the bench
  // the corner turns onto.
  let reach = depthAt(lay, back, n);
  for (const x of [-h, 0, h]) reach = Math.min(reach, firstCrossing(lay.outline, at(x, 0), n, 1));
  if (!(reach > TRESTLE_TIMBER) || reach > MAX_REACH) return null;
  const front = reach - FRONT_POST_SETBACK;
  // Its top under the lowest the bench comes over it.
  let top = Infinity;
  const steps = Math.max(2, Math.ceil(reach / 25));
  for (const x of [-h, 0, h]) {
    for (let i = 0; i <= steps; i++) {
      const z = undersideAt(lay, at(x, Math.min(reach - 0.5, Math.max(0.5, (reach * i) / steps))));
      if (z != null && z < top) top = z;
    }
  }
  if (!Number.isFinite(top) || top < SCULPT_TRESTLE_MIN_HEIGHT) return null;
  return { trestle: t, back, n, u, reach, front, top, solids: trestlePieces(at, front, top, t.id) };
}

const TRESTLES = contentMemo<SculptTrestleLayout[]>({ skip: new Set([...HEAVY_FIELDS, 'lights']) });

/** A sculpted bench's trestles, the ones that stand (memoised on its room and
 *  itself). */
export function sculptTrestles(inst: ProductInstance, sb: SculptedBench): SculptTrestleLayout[] {
  if (!sb.trestles?.length) return [];
  return TRESTLES.get(`${inst.id}/${sb.id}`, () => [inst, sb], () => trestlesOf(inst, sb));
}

function trestlesOf(inst: ProductInstance, sb: SculptedBench): SculptTrestleLayout[] {
  const lay = sb.trestles?.length ? sculptLayout(inst, sb) : null;
  if (!lay) return [];
  const out: SculptTrestleLayout[] = [];
  for (const t of sb.trestles ?? []) {
    const r = sculptTrestleLayout(inst, lay, t);
    if (r) out.push(r);
  }
  return out;
}

/** A trestle's plan footprint (room local): its band from the face to its
 *  reach, TRESTLE_WIDTH wide. */
export function sculptTrestleFootprint(tl: SculptTrestleLayout): Vec2[] {
  const h = TRESTLE_WIDTH / 2, d = Math.max(TRESTLE_TIMBER, tl.front);
  const at = (x: number, dd: number): Vec2 => ({ x: tl.back.x + tl.u.x * x + tl.n.x * dd, y: tl.back.y + tl.u.y * x + tl.n.y * dd });
  return [at(-h, 0), at(h, 0), at(h, d), at(-h, d)];
}
